import { useNavigate } from '@tanstack/react-router'

import { useMarkNotificationRead } from '@/shared/hooks/useNotifications'

import { deepLinkFor, timeAgo, visualFor } from './notification-config'

import type { Notification } from '@/shared/api/notificationsApi'

interface NotificationRowProps {
  notification: Notification
  /** Called after deep-link nav fires (use to close dropdown). */
  onNavigate?: () => void
  /** When true, hides the per-row "unread" left-edge stripe. */
  compact?: boolean
}

// Single row in the bell dropdown OR the full /notifications feed.
// Behaviour:
//   - tap → optimistic mark-read (via the mutation hook) + navigate per
//     deepLinkFor(). Mutation handles cache reconciliation.
//   - unread rows get a 4px brand-blue left-edge stripe + bold title.
export function NotificationRow({ notification, onNavigate, compact = false }: NotificationRowProps) {
  const navigate = useNavigate()
  const { mutate: markRead } = useMarkNotificationRead()

  const visual = visualFor(notification.type)
  const Icon = visual.icon
  const isUnread = !notification.read_at
  const link = deepLinkFor(notification)

  function handleClick() {
    if (isUnread) {
      markRead(notification.id)
    }
    if (link) {
      navigate(link)
    }
    onNavigate?.()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={
        isUnread && !compact
          ? 'flex w-full items-start gap-2.5 border-l-4 border-l-[#1a3db5] px-3 py-2.5 text-left hover:bg-blue-50/40 transition-colors'
          : 'flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors'
      }
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${visual.bg}`}
      >
        <Icon size={15} className={visual.fg} />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={
            isUnread
              ? 'text-xs font-bold text-gray-900 leading-snug'
              : 'text-xs font-semibold text-gray-700 leading-snug'
          }
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="mt-0.5 text-[11px] text-gray-500 leading-snug line-clamp-2">
            {notification.body}
          </p>
        )}
        <p className="mt-0.5 text-[10px] text-gray-400">{timeAgo(notification.created_at)}</p>
      </div>
    </button>
  )
}
